import * as React from "react";
import { WithRouterProps, withRouter } from "../../../helpers/RouterHelper";
import { useFetchStudyRecordComments } from "../../../hooks/http/useFetchStudyRecordComments";
import { useFetchCommentNotificationUnreadCount } from "../../../hooks/http/useFetchUnseenCount";
import { useQueryError } from "../../../hooks/http/useQueryError";
import { CommentInterface } from "../../../interfaces/CommentInterface";
import HeaderMenuFooter from "../../atoms/HeaderMenuFooter/index";
import HeaderNotificationsHeading from "../../atoms/HeaderNotificationsHeading";
import Icon from "../../atoms/Icon/index";
import HeaderPopup from "../../molecules/HeaderPopup/index";
import HeaderCommentNotificationList from "../HeaderCommentNotificationList";
import styles from "./styles.scss";

interface Props extends WithRouterProps {
  sectionId: string;
}

const HeaderCommentNotifications = (props: Props) => {
  const {
    listVisible,
    buttonRef,
    listRef,
    toggleListVisibility,
    unreadCount,
    allComments,
    isMessageFetching,
  } = useHeaderCommentNotifications(props);
  const { sectionId } = props;

  return (
    <div className={styles.root}>
      <button
        onClick={toggleListVisibility}
        className={styles.button}
        ref={buttonRef}
      >
        <Icon name="icon-notice" className={styles.iconNotice} />
        {renderBadge({ unreadCount: unreadCount?.count || 0 })}
      </button>
      {renderNotifications({
        listVisible,
        listRef,
        messages: allComments ?? [],
        isFetching: isMessageFetching,
        sectionId,
        toggleListVisibility,
      })}
    </div>
  );
};

const useHeaderCommentNotifications = (props: Props) => {
  const [listVisible, setListVisible] = React.useState(false);
  const buttonRef = React.useRef<HTMLButtonElement | null>(null);
  const listRef = React.useRef<HTMLSpanElement | null>(null);
  const [isEnabledFetchUnreadCount, setIsEnabledFetchUnreadCount] =
    React.useState(false);

  React.useEffect(() => {
    document.addEventListener("click", closeList);
    return () => {
      document.removeEventListener("click", closeList);
    };
  }, []);

  // 初回のレンダリング時に未読数を取得する
  React.useEffect(() => {
    setIsEnabledFetchUnreadCount(true);
  }, []);

  // ページ遷移時に未読数を取得する
  React.useEffect(() => {
    setIsEnabledFetchUnreadCount(true);
  }, [props.location.pathname]);

  const {
    data: unreadCount,
    error,
    isSuccess,
  } = useFetchCommentNotificationUnreadCount({
    sectionId: props.sectionId,
    pathname: props.location.pathname,
    enabled: isEnabledFetchUnreadCount,
  });
  // 成功時のコールバック処理
  React.useEffect(() => {
    if (isSuccess) {
      setIsEnabledFetchUnreadCount(false);
    }
  }, [isSuccess]);

  const {
    allComments,
    isFetching,
    error: messagesError,
  } = useFetchStudyRecordComments({
    sectionId: props.sectionId,
    enabled: listVisible,
  });
  useQueryError(error || messagesError);

  const closeList = (e: any) => {
    if (
      buttonRef &&
      buttonRef.current &&
      buttonRef.current.contains(e.target)
    ) {
      return;
    }

    if (listRef && listRef.current && listRef.current.contains(e.target)) {
      return;
    }

    setListVisible(false);
  };

  const toggleListVisibility = () => {
    setListVisible(!listVisible);
  };

  return {
    listVisible,
    buttonRef,
    listRef,
    toggleListVisibility,
    unreadCount,
    allComments,
    isMessageFetching: isFetching,
  };
};

type BadgeProps = {
  unreadCount: number;
};
const renderBadge = (props: BadgeProps) => {
  const unreadCountLabel =
    props.unreadCount > 999 ? "999+" : props.unreadCount + "";

  return props.unreadCount > 0 ? (
    <div className={styles.badge}>
      <div className={styles.number}>{unreadCountLabel}</div>
    </div>
  ) : null;
};

type NotificationsProps = {
  listVisible: boolean;
  listRef: React.RefObject<HTMLSpanElement | null> | null;
  messages: CommentInterface[];
  isFetching: boolean;
  sectionId: string;
  toggleListVisibility: () => void;
};

const renderNotifications = (props: NotificationsProps) => {
  if (props.listVisible) {
    return (
      <span ref={props.listRef}>
        <HeaderPopup>
          <HeaderNotificationsHeading title={"お知らせ"} />
          <HeaderCommentNotificationList
            loading={props.isFetching}
            comments={props.messages}
          />
          <HeaderMenuFooter
            path={`/sections/${props.sectionId}/study_records/comments`}
            label="もっと見る"
            onClick={props.toggleListVisibility}
          />
        </HeaderPopup>
      </span>
    );
  } else {
    return null;
  }
};

export default withRouter<Props>(HeaderCommentNotifications);
